import type { MyLibraryQuery, MyLibraryQueryVariables } from "@/generated/clip-source-pages";
import { shouldShowTranslationFailed } from '@/lib/projects/shouldShowTranslationFailed';
import { ClipSourceNotice } from "@/components/clips/ClipSourceNotice";
import { resolveClipSourceStatus, shouldPollClipSource } from "@/lib/clips/sourceReadiness";
import Layout from "@/components/Layout";
import { ChangeEvent, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHasFeature } from "@/lib/useHasFeature";
import { resolveRenderFont } from "@/lib/resolveRenderFont";
import ProFeatureDialog from "@/components/ProFeatureDialog";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { useWatermarkPosition } from "@/hooks/use-watermark-position";
import { WatermarkPositionContext } from "@/components/remotion/watermark/WatermarkPositionContext";
import { useAiAssistantAccess } from "@/hooks/use-ai-assistant-access";
import { aiStudioPathFor } from "@/lib/ai-assistant/aiStudioPaths";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useSourceSubtitlesEnabled } from "@/hooks/use-source-subtitles-enabled";
import { useWindowScroll } from "@uidotdev/usehooks";
import { useClipVersionGroups } from "@/hooks/use-clip-versions";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { GET_CLIPS_BY_LIBRARY_ID, GET_MY_LIBRARY, GET_MY_BRAND_KITS } from "@/queries/queries";
import { getProjectStatusLabel } from "@/constants/project-status";
import { MyClipsByLibraryIdQuery, MyClipsByLibraryIdQueryVariables, useRenderLibraryClipMutation, useDeleteMyClipMutation, useMyFontsQuery, useUpdateMyProjectMutation, useMyDataQuery, useFillerVideosQuery } from "@/generated/graphql";
import { appendFetchMoreList } from "@/lib/apollo/appendFetchMoreList";
import { Download, Palette, Film, Loader2 } from "lucide-react";
import { reduceMountedPreviewClipId } from "@/lib/clips/reduceMountedPreviewClipId";
import { deriveDetectionCropKeyframes, toCropCenterKeyframes } from "@/lib/crop/deriveDetectionCropKeyframes";
import VerticalClipCard from "@/components/VerticalClipCard";
import SkippedClipCard from "@/components/SkippedClipCard";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useSceneDetection } from "@/hooks/use-scene-detection";
import { useTranscription } from "@/hooks/use-transcription";
import { useShowWatermark } from "@/lib/useShowWatermark";
import { POST_LAYOUTS } from "@/lib/layouts/registry";
import { CIRCULO_SUBTITLE_SEED, shouldSeedCirculoSubtitle } from "@/lib/layouts/circuloSubtitleSeed";
import { buildPostLayoutSelection } from "@/lib/layouts/buildPostLayoutSelection";
import { buildBoundLayoutRenderInput } from "@/lib/export/buildBoundLayoutRenderInput";
import type { PostLayoutConfig, BrandKitLayoutSource, PostLayoutFieldValue } from "@/lib/layouts/types";
import { applyPostLayoutField } from "@/lib/layouts/applyPostLayoutField";
import {
  applyLayoutBinds,
  defaultLayoutBinds,
  resolveClipTitleForBind,
  sanitizeLayoutFieldBinds,
  type LayoutBindSource,
  type LayoutFieldBinds,
} from "@/lib/layouts/layoutFieldBinds";
import { applyMaterializedFillers } from "@/lib/layouts/fillerPlaylist";
import { fillerCatalogUrls } from "@/lib/layouts/filterFillerVideos";
import { resolveLayoutChannelSource } from "@/lib/layouts/resolveLayoutChannelSource";
import { applyTemplateToEditor } from "@/lib/brand-kit/applyTemplateToEditor";
import { resolveInitialBrandKitId } from "@/lib/brand-kit/resolveInitialBrandKitId";
import {
  gateForAppliedKit,
  gateForSavedSettings,
  OPEN_KIT_PREFILL_GATE,
  settleSelectedKit,
  type KitPrefillGate,
} from "@/lib/brand-kit/kitPrefillGate";
import { resolvePersistedProjectPreviewSettings } from "@/lib/preview/resolvePersistedProjectPreviewSettings";
import { useLibraryEffectivePostLayout, useLibraryEffectiveSubtitlePosition } from "@/hooks/use-effective-post-layout";
import { buildEnforcedInitialConfig, enforcedLockedKeys } from "@/lib/layouts/applyEnforcedLayout";
import { resolveEnforcedValue } from "@/lib/layouts/enforcedSubtitlePosition";
import { readKitSubtitleTextTransform } from "@/lib/subtitles/subtitleTextTransform";
import { useUploadFile } from "@/hooks/use-upload-file";
import { downloadClipVideo } from "@/lib/downloads/clip-download";
import { buildClipTranscriptionSegments } from "@/lib/preview/buildClipTranscriptionSegments";
import {
  buildPreviewProcessingByClipId,
  resolveFirstVisibleClipPreview,
} from "@/lib/preview/resolvePersistedClipPreview";
import { ProcessingStatus } from "@/components/ProcessingStatus";
import { ClipsLoadingStatus } from "@/components/ClipsLoadingStatus";
import { LoadingCard, LoadingCardFrame } from "@/components/LoadingCard";
import { etaSecondsToMinutes } from "@/lib/format/etaMinutes";
import {
  resolveClipsLoadingSteps,
  resolveClipsPageLoadingKind,
} from "@/lib/clips/resolveClipsPageLoading";
import { useClipPublishing } from "@/hooks/use-clip-publishing";
import { ClipPublishDialogs } from "@/components/publishing/ClipPublishDialogs";
import { SendToQueueDialog } from "@/components/publishQueues/SendToQueueDialog";
import { useSendToQueue } from "@/hooks/use-send-to-queue";
import { SOCIAL_PUBLISHING_FEATURE } from "@/lib/publishing/socialPublishingFeature";
import ClipsStyleSheet from "@/components/clips/ClipsStyleSheet";
import { EditorTemplatesCarousel } from "@/components/editor/EditorTemplatesCarousel";
import { useDownloadUpsell } from "@/contexts/downloadUpsellContext";
import { pickSubtitleOutline, subtitleOutlineFromKit, subtitleOutlineToKit } from "@/lib/subtitles/subtitleOutline";
import {
  defaultSubtitleData,
  defaultLogoData,
} from "@/components/clips/clipsStyleDefaults";
import { DEFAULT_WATERMARK_POSITION } from '@/lib/watermark/watermarkPosition';
import { FailedProjectNotice } from "@/components/projects/FailedProjectNotice";

const getAlignItems = (position: string) => {
  if (position.includes("top")) return "flex-start";
  if (position.includes("bottom")) return "flex-end";
  return "center";
};

const getJustifyContent = (position: string) => {
  if (position.includes("left")) return "flex-start";
  if (position.includes("right")) return "flex-end";
  return "center";
};

const LibraryClips = () => {
  const { t, i18n } = useTranslation();
  const { libraryId } = useParams<{ libraryId: string }>();
  const showWatermark = useShowWatermark();
  const PAGE_SIZE = 24;

  const { data: libraryData, loading: libraryLoading, refetch: refetchLibrary } = useQuery<MyLibraryQuery, MyLibraryQueryVariables>(
    GET_MY_LIBRARY,
    { variables: { id: libraryId! }, skip: !libraryId }
  );

  const { data, loading, error, fetchMore, refetch } = useQuery<MyClipsByLibraryIdQuery, MyClipsByLibraryIdQueryVariables>(
    GET_CLIPS_BY_LIBRARY_ID,
    {
      variables: { limit: PAGE_SIZE, offset: 0, library_id: libraryId!, sort: 'clip_order:asc' },
      fetchPolicy: 'no-cache',
      skip: !libraryId || libraryData?.myLibrary?.curation_status !== 'success',
    }
  );

  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [selectedClips, setSelectedClips] = useState<Set<string>>(new Set());
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;
  const navigate = useNavigate();
  const hasProFeatures = useHasFeature('pro_features');
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  // Bulk export (select-all + export several at once) is PRO-only; free users
  // export one clip at a time.
  const [bulkProDialogOpen, setBulkProDialogOpen] = useState(false);
  const { toast } = useToast();
  const { notifyClipDownloaded } = useDownloadUpsell();
  const { dialog: quotaUpsellDialog, handleQuotaError } = useQuotaUpsellDialog();

  // Brand kit state
  const [subtitlesEnabled, setSubtitlesEnabled] = useSourceSubtitlesEnabled(libraryData?.myLibrary);
  const [styleSheetOpen, setStyleSheetOpen] = useState(false);
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<string | null>(null);
  const [renderLibraryClipMutation] = useRenderLibraryClipMutation();
  const [deleteMyClipMutation] = useDeleteMyClipMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportingClipIds, setExportingClipIds] = useState<Set<string>>(new Set());
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  // Editor state
  const [subtitleData, setSubtitleData] = useState({ ...defaultSubtitleData });
  const [logoData, setLogoData] = useState({ ...defaultLogoData });
  const [selectedSubtitleFontId, setSelectedSubtitleFontId] = useState('');
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [postLayout, setPostLayout] = useState<PostLayoutConfig | null>(null);
  const [layoutFieldBinds, setLayoutFieldBinds] = useState<LayoutFieldBinds>({});
  const [selectedSpeakerLayout, setSelectedSpeakerLayout] = useState(1);
  const postLayoutRef = useRef<PostLayoutConfig | null>(null);
  postLayoutRef.current = postLayout;

  const { data: brandKitsData } = useQuery(GET_MY_BRAND_KITS);
  const { data: fontsData } = useMyFontsQuery();
  const { data: myData } = useMyDataQuery();
  const { data: fillerVideosData } = useFillerVideosQuery();
  const { getUploadUrl } = useUploadFile();

  const brandKits = useMemo(() => brandKitsData?.myBrandKits ?? [], [brandKitsData?.myBrandKits]);
  const userBrandKits = useMemo(() => brandKits.filter((kit: any) => !kit.is_public), [brandKits]);
  const publicBrandKits = useMemo(() => brandKits.filter((kit: any) => kit.is_public), [brandKits]);
  const selectedBrandKit = brandKits.find((kit: any) => kit.id === selectedBrandKitId);

  const clips = useMemo(() => data?.myClips ?? [], [data?.myClips]);

  // Version grouping: group re-exported clips as versions of the same clip
  const { groups: versionGroups, getGroupForClip } = useClipVersionGroups(clips);

  // Clip IDs that are older versions (hidden from the grid, only latest version shown)
  const hiddenVersionIds = useMemo(() => {
    const hidden = new Set<string>();
    for (const group of versionGroups) {
      for (let i = 1; i < group.allVersions.length; i++) {
        hidden.add(group.allVersions[i].id);
      }
    }
    return hidden;
  }, [versionGroups]);

  const visibleClips = useMemo(
    () => clips.filter((clip) => !hiddenVersionIds.has(clip.id)),
    [clips, hiddenVersionIds],
  );

  // User-confirmed, PRO-gated TikTok and YouTube publishing for visible cards.
  const {
    getPublishStatus,
    getPublishFailReason,
    getPublishMediaKinds,
    showPublishProGate,
    setShowPublishProGate,
    publishTarget,
    setPublishTarget,
    resolveClipTitle,
    resolvePublishHandler,
    refetchPublishes,
  } = useClipPublishing(visibleClips);
  const {
    targetClipId: queueTargetClipId,
    handleSendToQueue,
    setOpen: setQueueDialogOpen,
  } = useSendToQueue({
    hasProFeatures,
    onProGate: () => setShowPublishProGate(true),
  });

  // Track clip count in a ref so refetch calls always request enough clips
  // (re-export creates duplicate clips that must appear in the refetched results)
  const clipsCountRef = useRef(PAGE_SIZE);
  clipsCountRef.current = Math.max(clips.length, PAGE_SIZE);
  const library = libraryData?.myLibrary;
  const sourceStatus = resolveClipSourceStatus(library?.source_status);
  const sourceReady = sourceStatus === 'ready';
  const {
    position: watermarkPosition,
    setPosition: setWatermarkPosition,
    resetPosition: resetWatermarkPosition,
  } = useWatermarkPosition({
    parentId: library?.id ?? null,
    loadedPosition: (library as any)?.watermark_position,
    enabled: !!library?.id,
  });
  const libraryVideoUrl = library?.compressed_cut_video_url || library?.compressed_base_video_url || library?.cut_video_url || library?.base_video_url || '';
  const channelSource = useMemo(
    () => resolveLayoutChannelSource(library as { youtube_channels?: Array<{ name?: string | null; thumbnail?: string | null }> | null } | null | undefined),
    [library],
  );
  const fillerPool = useMemo(
    () => fillerCatalogUrls(fillerVideosData?.fillerVideos ?? [], i18n.language),
    [fillerVideosData?.fillerVideos, i18n.language],
  );
  const previewFillerDurationSec = clips[0]?.duration_seconds ?? library?.duration_seconds ?? 8;
  const postLayoutWithFillers = useMemo(() => {
    if (!postLayout) return null;
    return applyMaterializedFillers(postLayout, {
      pool: fillerPool,
      targetDurationSec: previewFillerDurationSec,
    });
  }, [postLayout, fillerPool, previewFillerDurationSec]);

  const {
    data: sceneDetection,
    status: sceneDetectionStatus,
    error: sceneDetectionError,
    timedOut: sceneDetectionTimedOut,
  } = useSceneDetection(library?.scene_detection_url, library?.scene_detection);
  const {
    data: transcription,
    status: transcriptionStatus,
    error: transcriptionError,
    timedOut: transcriptionTimedOut,
  } = useTranscription((library as any)?.transcription_url, (library as any)?.transcription);

  // Extract words from library transcription
  const transcriptionWords = useMemo(() => {
    return transcription?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
  }, [transcription]);

  const previewProcessingByClipId = useMemo(
    () => buildPreviewProcessingByClipId(clips, {
      sceneDetection,
      isPortrait: true,
      projectDuration: library?.duration_seconds,
    }),
    [clips, sceneDetection, library?.duration_seconds],
  );

  const firstPreviewProcessing = useMemo(
    () => resolveFirstVisibleClipPreview(clips, hiddenVersionIds, previewProcessingByClipId),
    [clips, hiddenVersionIds, previewProcessingByClipId],
  );

  // Font loading
  const loadFont = useCallback((fontId: string) => {
    if (!fontsData?.myFonts || loadedFonts.has(fontId)) return;
    const font = fontsData.myFonts.find(f => f.id === fontId);
    if (!font || !font.file_url) return;
    let styleElement = document.getElementById(`font-loader-${fontId}`) as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = `font-loader-${fontId}`;
      styleElement.textContent = `@font-face { font-family: '${fontId}'; src: url('${font.file_url}') format('truetype'); font-display: swap; }`;
      document.head.appendChild(styleElement);
    }
    setLoadedFonts(prev => new Set([...prev, fontId]));
  }, [fontsData?.myFonts, loadedFonts]);

  const getFontFamilyName = useCallback((fontId: string) => {
    if (!fontId || !loadedFonts.has(fontId)) return null;
    return fontId;
  }, [loadedFonts]);

  const getPositionFromAlignItems = useCallback((alignItems: string, justifyContent: string) => {
    const vMap: Record<string, string> = { 'flex-start': 'top', 'center': 'middle', 'flex-end': 'bottom' };
    const hMap: Record<string, string> = { 'flex-start': 'left', 'center': 'center', 'flex-end': 'right' };
    return `${vMap[alignItems] || 'bottom'}-${hMap[justifyContent] || 'center'}`;
  }, []);

  // Campaign-enforced post layout (null when no active enforcement). Declared
  // before the brand kit loader so the enforced subtitle margin can override it.
  const effectivePostLayout = useLibraryEffectivePostLayout(libraryId ?? null);
  // Campaign subtitle position: brand-kit-style anchor + vertical margin.
  const enforcedSubtitlePos = useLibraryEffectiveSubtitlePosition(libraryId ?? null);
  // Last values the user set by hand; null until they touch the controls.
  const userMarginVRef = useRef<number | null>(null);
  const userAlignRef = useRef<string | null>(null);
  const kitPrefillGateRef = useRef<KitPrefillGate>(OPEN_KIT_PREFILL_GATE);
  const restoredLibrarySettingsKeyRef = useRef<string | null>(null);

  // The subtitle/logo/layout settings saved in the library editor, resolved
  // the same way as on Project Clips and in the editor.
  const persistedLibraryPreviewSettings = useMemo(() => {
    if (!library) return null;
    return resolvePersistedProjectPreviewSettings({
      project: library,
      brandKits,
      hasProFeatures,
      defaultSubtitleData,
      defaultLogoData,
      effectivePostLayout,
      effectiveSubtitlePosition: enforcedSubtitlePos,
    });
  }, [library, brandKits, hasProFeatures, effectivePostLayout, enforcedSubtitlePos]);

  // Apply the campaign anchor + margin when they resolve (overrides the brand
  // kit; manual user edits win unless the campaign locked the position).
  useEffect(() => {
    if (!enforcedSubtitlePos) return;
    const { locked, alignItems, verticalMargin } = enforcedSubtitlePos;
    setSubtitleData(prev => ({
      ...prev,
      ...(locked || userMarginVRef.current == null ? { marginVertical: verticalMargin } : {}),
      ...(locked || userAlignRef.current == null
        ? { position: getPositionFromAlignItems(alignItems, 'center') }
        : {}),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enforcedSubtitlePos]);

  // Load brand kit into editor
  const loadBrandKitIntoEditor = useCallback((kit: any) => {
    if (!kit?.data) return;
    const sub = kit.data.subtitle || {};
    const logo = kit.data.logo || {};
    setSubtitleData({
      fontFamily: sub.subtitle_font_id || defaultSubtitleData.fontFamily,
      fontSize: sub.subtitle_font_size || defaultSubtitleData.fontSize,
      textColor: sub.subtitle_color || defaultSubtitleData.textColor,
      highlightColor: sub.subtitle_highlight_color || defaultSubtitleData.highlightColor,
      highlightEnabled: sub.subtitle_highlight_enabled ?? defaultSubtitleData.highlightEnabled,
      highlightBoxEnabled: sub.highlight_box_enabled ?? defaultSubtitleData.highlightBoxEnabled,
      highlightBoxBackgroundColor: sub.highlight_box_background_color || defaultSubtitleData.highlightBoxBackgroundColor,
      highlightBoxBorderRadius: sub.highlight_box_border_radius ?? defaultSubtitleData.highlightBoxBorderRadius,
      highlightBoxPaddingX: sub.highlight_box_padding_x ?? defaultSubtitleData.highlightBoxPaddingX,
      highlightBoxPaddingY: sub.highlight_box_padding_y ?? defaultSubtitleData.highlightBoxPaddingY,
      backgroundColor: sub.background_color || defaultSubtitleData.backgroundColor,
      backgroundEnabled: sub.background_enabled ?? defaultSubtitleData.backgroundEnabled,
      backgroundOpacity: sub.background_opacity ?? defaultSubtitleData.backgroundOpacity,
      backgroundWidth: sub.background_width ?? defaultSubtitleData.backgroundWidth,
      backgroundHeight: sub.background_height ?? defaultSubtitleData.backgroundHeight,
      position: getPositionFromAlignItems(
        resolveEnforcedValue<string>({
          enforced: enforcedSubtitlePos
            ? { value: enforcedSubtitlePos.alignItems, locked: enforcedSubtitlePos.locked }
            : null,
          brandKitValue: sub.align_items,
          userValue: userAlignRef.current,
          defaultValue: 'flex-end',
        }),
        sub.justify_content || 'center',
      ),
      marginVertical: resolveEnforcedValue<number>({
        enforced: enforcedSubtitlePos
          ? { value: enforcedSubtitlePos.verticalMargin, locked: enforcedSubtitlePos.locked }
          : null,
        brandKitValue: sub.vertical_margin_percentage,
        userValue: userMarginVRef.current,
        defaultValue: defaultSubtitleData.marginVertical,
      }),
      marginHorizontal: sub.horizontal_margin_percentage ?? defaultSubtitleData.marginHorizontal,
      shadowEnabled: sub.shadow_enabled ?? defaultSubtitleData.shadowEnabled,
      shadowColor: sub.shadow_color || defaultSubtitleData.shadowColor,
      shadowSize: sub.shadow_size ?? defaultSubtitleData.shadowSize,
      ...subtitleOutlineFromKit(sub),
      animation: sub.animation || defaultSubtitleData.animation,
      textAlign: defaultSubtitleData.textAlign,
      textTransform: readKitSubtitleTextTransform(sub),
      removePunctuation: sub.remove_punctuation ?? defaultSubtitleData.removePunctuation,
      maxWordsPerLine: sub.max_words_per_line ?? defaultSubtitleData.maxWordsPerLine,
    });
    setLogoData({
      url: logo.logo_image_url || null,
      position: logo.logo_position || defaultLogoData.position,
      size: logo.logo_size ?? defaultLogoData.size,
      opacity: logo.logo_opacity ?? defaultLogoData.opacity,
      horizontalMargin: logo.logo_horizontal_margin ?? defaultLogoData.horizontalMargin,
      verticalMargin: logo.logo_vertical_margin ?? defaultLogoData.verticalMargin,
    });
    if (sub.subtitle_font_id) {
      setSelectedSubtitleFontId(sub.subtitle_font_id);
      loadFont(sub.subtitle_font_id);
    }
    const applied = applyTemplateToEditor(postLayoutRef.current, kit.data);
    setPostLayout(applied.postLayout);
    setLayoutFieldBinds(applied.fieldBinds);
    setSelectedSpeakerLayout(1);
  }, [getPositionFromAlignItems, loadFont, enforcedSubtitlePos]);

  // Restore the settings saved in the library editor. With a saved
  // subtitle_config, an automatically selected kit must not overwrite them
  // (the gate below); a kit the user picks still applies.
  useEffect(() => {
    if (!library || !persistedLibraryPreviewSettings) return;
    const restorationKey = JSON.stringify([
      library.id,
      (library as any).subtitle_config ?? null,
      (library as any).post_layout ?? null,
    ]);
    if (restoredLibrarySettingsKeyRef.current === restorationKey) return;
    restoredLibrarySettingsKeyRef.current = restorationKey;

    kitPrefillGateRef.current = gateForSavedSettings({
      hasSavedSettings: Boolean((library as any).subtitle_config),
      savedKitId: library.brand_kit?.id,
    });
    setSubtitleData(persistedLibraryPreviewSettings.subtitleData);
    setLogoData(persistedLibraryPreviewSettings.logoData);
    setSelectedSubtitleFontId(persistedLibraryPreviewSettings.fontId);
    if (persistedLibraryPreviewSettings.fontId) {
      loadFont(persistedLibraryPreviewSettings.fontId);
    }
    setPostLayout(persistedLibraryPreviewSettings.postLayout);
    setLayoutFieldBinds(persistedLibraryPreviewSettings.fieldBinds);
  }, [library, persistedLibraryPreviewSettings, loadFont]);

  // Start on the template chosen when the video was submitted
  // (library.brand_kit), else the default kit. Wait for the library: picking
  // as soon as the kits loaded ignored the chosen template, so clips exported
  // with the default (or newest public) kit. A late plan answer re-resolves,
  // unless the user already picked a kit.
  const initialBrandKitKeyRef = useRef<string | null>(null);
  const autoSelectedBrandKitIdRef = useRef<string | null>(null);
  useEffect(() => {
    const library = libraryData?.myLibrary;
    if (!library || brandKits.length === 0) return;
    const savedBrandKitId = library.brand_kit?.id ?? null;
    const key = `${library.id}:${savedBrandKitId ?? ''}:${hasProFeatures}`;
    if (initialBrandKitKeyRef.current === key) return;
    initialBrandKitKeyRef.current = key;
    if (selectedBrandKitId && selectedBrandKitId !== autoSelectedBrandKitIdRef.current) return;
    const kitId = resolveInitialBrandKitId({ savedBrandKitId, kits: brandKits, hasProFeatures });
    const kit = brandKits.find((candidate: any) => candidate.id === kitId);
    if (!kit || kit.id === selectedBrandKitId) return;
    autoSelectedBrandKitIdRef.current = kit.id;
    setSelectedBrandKitId(kit.id);
  }, [libraryData?.myLibrary, brandKits, hasProFeatures, selectedBrandKitId]);

  // Apply an automatically selected kit only when it genuinely changes and
  // no saved settings were restored (same rule as Project Clips and the editor).
  useEffect(() => {
    if (!selectedBrandKitId) return;
    const kit = brandKits.find((candidate: any) => candidate.id === selectedBrandKitId);
    if (!kit) return;
    const settled = settleSelectedKit(kitPrefillGateRef.current, kit.id);
    kitPrefillGateRef.current = settled.gate;
    if (!settled.prefill) return;
    loadBrandKitIntoEditor(kit);
  }, [selectedBrandKitId, brandKits, loadBrandKitIntoEditor]);

  // Compute logoConfig from editor state for skipped clip previews
  const logoConfig = useMemo(() => {
    if (!subtitlesEnabled || !logoData.url) return undefined;
    return {
      url: logoData.url,
      position: logoData.position,
      size: logoData.size,
      opacity: logoData.opacity,
      horizontalMargin: logoData.horizontalMargin,
      verticalMargin: logoData.verticalMargin,
    };
  }, [logoData, subtitlesEnabled]);

  // Compute subtitleConfig from editor state for skipped clip previews
  const subtitleConfig = useMemo(() => {
    return {
      fontFamily: getFontFamilyName(selectedSubtitleFontId) || subtitleData.fontFamily,
      fontSize: subtitleData.fontSize,
      textColor: subtitleData.textColor,
      highlightColor: subtitleData.highlightColor,
      highlightEnabled: subtitleData.highlightEnabled,
      highlightBoxEnabled: subtitleData.highlightBoxEnabled,
      highlightBoxBackgroundColor: subtitleData.highlightBoxBackgroundColor,
      highlightBoxBorderRadius: subtitleData.highlightBoxBorderRadius,
      highlightBoxPaddingX: subtitleData.highlightBoxPaddingX,
      highlightBoxPaddingY: subtitleData.highlightBoxPaddingY,
      backgroundColor: subtitleData.backgroundColor,
      backgroundEnabled: subtitleData.backgroundEnabled,
      backgroundOpacity: subtitleData.backgroundOpacity,
      backgroundWidth: subtitleData.backgroundWidth,
      backgroundHeight: subtitleData.backgroundHeight,
      position: subtitleData.position,
      marginVertical: subtitleData.marginVertical,
      marginHorizontal: subtitleData.marginHorizontal,
      shadowEnabled: subtitleData.shadowEnabled,
      shadowColor: subtitleData.shadowColor,
      shadowSize: subtitleData.shadowSize,
      ...pickSubtitleOutline(subtitleData),
      animation: subtitleData.animation,
      textAlign: subtitleData.textAlign,
      lineSpacing: 1.2,
      animationSpeed: 1.0,
      maxWordsPerLine: subtitleData.maxWordsPerLine,
      removePunctuation: subtitleData.removePunctuation,
    };
  }, [subtitleData, selectedSubtitleFontId, loadedFonts, getFontFamilyName]);

  const fallbackPreviewKit = useMemo(() => {
    if (!selectedBrandKit) return null;
    return {
      ...selectedBrandKit,
      data: {
        ...selectedBrandKit.data,
        subtitle: {
          subtitle_color: subtitleData.textColor,
          subtitle_font_id: selectedSubtitleFontId || "",
          subtitle_font_size: subtitleData.fontSize,
          subtitle_highlight_color: subtitleData.highlightColor,
          subtitle_highlight_enabled: subtitleData.highlightEnabled,
          highlight_box_enabled: subtitleData.highlightBoxEnabled,
          highlight_box_background_color: subtitleData.highlightBoxBackgroundColor,
          highlight_box_border_radius: subtitleData.highlightBoxBorderRadius,
          highlight_box_padding_x: subtitleData.highlightBoxPaddingX,
          highlight_box_padding_y: subtitleData.highlightBoxPaddingY,
          vertical_margin_percentage: subtitleData.marginVertical,
          align_items: getAlignItems(subtitleData.position),
          animation: subtitleData.animation,
          background_color: subtitleData.backgroundColor,
          background_enabled: subtitleData.backgroundEnabled,
          background_height: subtitleData.backgroundHeight,
          background_opacity: subtitleData.backgroundOpacity,
          background_width: subtitleData.backgroundWidth,
          horizontal_margin_percentage: subtitleData.marginHorizontal,
          justify_content: getJustifyContent(subtitleData.position),
          max_words_per_line: subtitleData.maxWordsPerLine,
          shadow_color: subtitleData.shadowColor,
          shadow_enabled: subtitleData.shadowEnabled,
          shadow_size: subtitleData.shadowSize,
          ...subtitleOutlineToKit(subtitleData),
          text_transform: subtitleData.textTransform,
          remove_punctuation: subtitleData.removePunctuation,
        },
        logo: {
          logo_image_url: logoData.url || null,
          logo_position: logoData.position,
          logo_size: logoData.size,
          logo_opacity: logoData.opacity,
          logo_horizontal_margin: logoData.horizontalMargin,
          logo_vertical_margin: logoData.verticalMargin,
        },
      },
    };
  }, [selectedBrandKit, selectedSubtitleFontId, subtitleData, logoData]);

  // Transcription segments for the live preview (first clip)
  const previewTranscriptionSegments = useMemo(() => {
    if (
      !subtitlesEnabled
      || !firstPreviewProcessing?.intervals.length
      || transcriptionWords.length === 0
    ) {
      return undefined;
    }
    const words = transcriptionWords.map((w: any) => ({
      word: w.word || w.punctuated_word || w.text || '',
      start: w.start ?? w.startTime ?? w.start_time ?? 0,
      end: w.end ?? w.endTime ?? w.end_time ?? 0,
    }));
    return buildClipTranscriptionSegments({
      words,
      intervals: firstPreviewProcessing.intervals,
      projectDuration: library?.duration_seconds ?? Infinity,
    });
  }, [
    transcriptionWords,
    firstPreviewProcessing,
    subtitlesEnabled,
    library?.duration_seconds,
  ]);

  // Editor helpers
  const updateSubtitle = (field: string, value: any) => {
    if (field === 'marginVertical') {
      if (enforcedSubtitlePos?.locked) return; // position enforced by campaign
      userMarginVRef.current = value;
    }
    if (field === 'position') {
      if (enforcedSubtitlePos?.locked) return; // position enforced by campaign
      userAlignRef.current = getAlignItems(value);
    }
    setSubtitleData(prev => ({ ...prev, [field]: value }));
  };
  const updateLogo = (field: string, value: any) => {
    setLogoData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: t('configureSubtitles.toast.error'), description: t('common.fileTooLarge', { size: '5MB' }), variant: "destructive" });
      event.target.value = '';
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast({ title: t('configureSubtitles.toast.error'), description: t('common.invalidFileType', { types: 'PNG, JPG' }), variant: "destructive" });
      event.target.value = '';
      return;
    }
    setIsUploadingLogo(true);
    try {
      const uploadUrl = await getUploadUrl(file);
      if (uploadUrl) updateLogo("url", uploadUrl);
    } catch (error) {
      console.error("Error uploading logo:", error);
    } finally {
      setIsUploadingLogo(false);
      event.target.value = '';
    }
  };

  const handleSelectBrandKit = useCallback((kit: any) => {
    setSelectedBrandKitId(kit.id);
    kitPrefillGateRef.current = gateForAppliedKit(kit.id);
    loadBrandKitIntoEditor(kit);
  }, [loadBrandKitIntoEditor]);

  const handleSelectSubtitleFont = useCallback((fontId: string, font: any) => {
    setSelectedSubtitleFontId(fontId);
    updateSubtitle("fontFamily", font.family);
    loadFont(fontId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFont]);

  const [mountedPreviewClipId, setMountedPreviewClipId] = useState<string | null>(null);
  const handlePreviewPlay = useCallback((clipId: string) => {
    setMountedPreviewClipId((current) => reduceMountedPreviewClipId(current, { type: 'play', clipId }));
  }, []);
  const handlePreviewPause = useCallback((clipId: string) => {
    setMountedPreviewClipId((current) => reduceMountedPreviewClipId(current, { type: 'pause', clipId }));
  }, []);

  // Card previews without their own saved framing use the editor's
  // auto-framing rule (zoom and facecam handling included), so a card shows
  // what the export renders.
  const cropCenterKeyframes = useMemo(
    () => toCropCenterKeyframes(deriveDetectionCropKeyframes({
      detection: sceneDetection,
      isPortrait: true,
    })),
    [sceneDetection],
  );

  // Count unique speakers (face_ids) from scene detection
  const uniqueSpeakers = useMemo(() => {
    if (!sceneDetection) return [];
    const data = Array.isArray(sceneDetection) ? sceneDetection : [sceneDetection];
    const detectionScenes = data.flatMap((d: any) => d.scenes || []);
    const faceMap = new Map<string, { face_id: string; totalTime: number; scenes: Array<{ start: number; end: number; x: number; y: number; width: number; height: number }> }>();
    for (const scene of detectionScenes) {
      if (typeof scene.start_time !== 'number' || typeof scene.end_time !== 'number') continue;
      const faces = scene.faces || [];
      for (const face of faces) {
        if (!face.face_id) continue;
        if (!faceMap.has(face.face_id)) {
          faceMap.set(face.face_id, { face_id: face.face_id, totalTime: 0, scenes: [] });
        }
        const entry = faceMap.get(face.face_id)!;
        entry.totalTime += scene.end_time - scene.start_time;
        entry.scenes.push({
          start: scene.start_time,
          end: scene.end_time,
          x: face.x,
          y: face.y,
          width: face.width,
          height: face.height,
        });
      }
    }
    // Sort by total screen time descending
    return Array.from(faceMap.values()).sort((a, b) => b.totalTime - a.totalTime);
  }, [sceneDetection]);

  const [updateMyProjectMutation] = useUpdateMyProjectMutation();
  const speakerLayoutDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    const persisted = (library as { speaker_layout?: number } | undefined)?.speaker_layout;
    if (typeof persisted === "number" && persisted !== selectedSpeakerLayout) {
      setSelectedSpeakerLayout(persisted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(library as { speaker_layout?: number } | undefined)?.speaker_layout]);

  const handleSpeakerLayoutChange = useCallback(
    (next: number) => {
      setSelectedSpeakerLayout(next);
      if (!(library as any)?.id) return;
      if (speakerLayoutDebounceRef.current != null) {
        window.clearTimeout(speakerLayoutDebounceRef.current);
      }
      speakerLayoutDebounceRef.current = window.setTimeout(() => {
        updateMyProjectMutation({
          variables: { id: (library as any).id, input: { speaker_layout: next } },
        }).catch((err) => console.error("Failed to persist speaker_layout", err));
        speakerLayoutDebounceRef.current = null;
      }, 400);
    },
    [(library as any)?.id, updateMyProjectMutation],
  );

  useEffect(() => {
    if (!effectivePostLayout) return;
    setPostLayout((prev) =>
      buildEnforcedInitialConfig(
        effectivePostLayout,
        prev ? (prev as unknown as { kind: string } & Record<string, unknown>) : null,
      ),
    );
  }, [effectivePostLayout]);

  const brandKitLayoutSource = useMemo<BrandKitLayoutSource | null>(() => {
    if (!selectedBrandKit) return null;
    return {
      logoUrl: selectedBrandKit.data?.logo?.logo_image_url ?? undefined,
      avatarUrl: selectedBrandKit.data?.logo?.logo_image_url ?? undefined,
      accentColor: selectedBrandKit.data?.colors?.[0] ?? undefined,
      handle: undefined,
      displayName: undefined,
    };
  }, [selectedBrandKit]);

  const selectPostLayout = useCallback((kind: keyof typeof POST_LAYOUTS, options?: { preset?: string }) => {
    const built = buildPostLayoutSelection(kind, options, {
      clipTitle: clips[0]?.name,
      channelName: channelSource?.name,
      channelAvatarUrl: channelSource?.thumbnail,
    }, brandKitLayoutSource, postLayout);
    setPostLayout(built);
    setLayoutFieldBinds(defaultLayoutBinds(kind));
    setSelectedSpeakerLayout(1);
    if (shouldSeedCirculoSubtitle(postLayout?.kind ?? null, kind)) {
      setSubtitleData((prev) => ({ ...prev, ...CIRCULO_SUBTITLE_SEED }));
      setSelectedSubtitleFontId(CIRCULO_SUBTITLE_SEED.fontFamily);
    }
  }, [brandKitLayoutSource, clips, channelSource, postLayout]);

  const updatePostLayoutField = useCallback((key: string, value: PostLayoutFieldValue) => {
    setPostLayout((prev) => (prev ? applyPostLayoutField(prev, key, value, false) : prev));
  }, []);

  const handleFieldBindChange = useCallback((key: string, source: LayoutBindSource | null) => {
    if (!postLayout) return;
    setLayoutFieldBinds((prev) => {
      const next = { ...prev };
      if (source) next[key] = source;
      else delete next[key];
      return sanitizeLayoutFieldBinds(postLayout.kind, next);
    });
  }, [postLayout]);

  const bindContext = useMemo(() => ({
    clipTitle: resolveClipTitleForBind(clips[0]?.name),
    channelName: channelSource?.name,
    channelAvatarUrl: channelSource?.thumbnail,
    userName: myData?.myData?.name ?? undefined,
    userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
    hasChannel: !!channelSource,
  }), [clips, channelSource, myData]);

  const boundPostLayoutForClip = useCallback((clipName?: string | null) => {
    if (!postLayoutWithFillers) return null;
    return applyLayoutBinds(postLayoutWithFillers, layoutFieldBinds, {
      clipTitle: resolveClipTitleForBind(clipName),
      channelName: channelSource?.name,
      channelAvatarUrl: channelSource?.thumbnail,
      userName: myData?.myData?.name ?? undefined,
      userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
    });
  }, [postLayoutWithFillers, layoutFieldBinds, channelSource, myData]);

  const boundLayoutRenderInputForClip = useCallback((clip?: {
    name?: string | null;
    duration_seconds?: number | null;
  }) => buildBoundLayoutRenderInput({
    postLayout,
    fieldBinds: layoutFieldBinds,
    clipTitle: resolveClipTitleForBind(clip?.name),
    channelName: channelSource?.name,
    channelAvatarUrl: channelSource?.thumbnail,
    userName: myData?.myData?.name ?? undefined,
    userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
    speakerLayout: selectedSpeakerLayout,
    uniqueSpeakerCount: uniqueSpeakers.length,
    fillerPool,
    clipDurationSec: clip?.duration_seconds ?? undefined,
  }), [
    postLayout,
    layoutFieldBinds,
    channelSource,
    myData,
    selectedSpeakerLayout,
    uniqueSpeakers.length,
    fillerPool,
  ]);

  useEffect(() => {
    return () => {
      if (speakerLayoutDebounceRef.current != null) {
        window.clearTimeout(speakerLayoutDebounceRef.current);
      }
    };
  }, []);

  // Build scene timeline from raw scene detection (which face_ids are present per scene)
  const sceneTimeline = useMemo(() => {
    if (!sceneDetection) return [];
    const data = Array.isArray(sceneDetection) ? sceneDetection : [sceneDetection];
    const detectionScenes = data.flatMap((d: any) => d.scenes || []);
    return detectionScenes
      .filter((s: any) => typeof s.start_time === 'number' && typeof s.end_time === 'number')
      .map((s: any) => ({
        start: s.start_time,
        end: s.end_time,
        faceIds: ((s.faces || []) as any[]).map((f: any) => f.face_id).filter(Boolean) as string[],
      }));
  }, [sceneDetection]);

  // Build speaker layout config for the live preview
  const previewSpeakerLayout = useMemo(() => {
    if (selectedSpeakerLayout <= 1 || uniqueSpeakers.length <= 1) return undefined;
    const numSpeakers = Math.min(selectedSpeakerLayout, uniqueSpeakers.length);
    const speakers = uniqueSpeakers.slice(0, numSpeakers).map((entry) => ({
      face_id: entry.face_id,
      scenes: entry.scenes.map((s) => ({
        start: s.start,
        end: s.end,
        cropCenter: {
          x: (s.x + s.width / 2) * 100,
          y: (s.y + s.height / 2) * 100,
        },
      })),
    }));
    return { numSpeakers, speakers, sceneTimeline };
  }, [selectedSpeakerLayout, uniqueSpeakers, sceneTimeline]);

  const sortByOrder = (a: typeof clips[0], b: typeof clips[0]) => {
    const orderA = a.clip_order ?? Infinity;
    const orderB = b.clip_order ?? Infinity;
    if (orderA !== orderB) return orderA - orderB;
    const scoreA = a.clip_score ?? 0;
    const scoreB = b.clip_score ?? 0;
    if (scoreA !== scoreB) return scoreB - scoreA;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  };

  // Track which clips were originally in the preview section so they don't move when exported.
  // A clip is originally skipped if clipping & preparing are both skipped (rendering may change later).
  // New clips (e.g. duplicates from re-export) are added to the set if they match the skipped criteria.
  const originalSkippedIds = useRef<Set<string> | null>(null);
  const skippedIdSet = useMemo(() => {
    if (clips.length === 0) return new Set<string>();
    if (!originalSkippedIds.current) {
      // First load: compute from scratch
      const ids = new Set(
        clips
          .filter(c => c.clipping_status === 'skipped' && c.preparing_status === 'skipped')
          .map(c => c.id)
      );
      originalSkippedIds.current = ids;
      return ids;
    }
    // Subsequent loads: add any new clips that match skipped criteria
    // (e.g. duplicates from re-export inherit clipping/preparing status from the original)
    let updated = false;
    for (const c of clips) {
      if (c.clipping_status === 'skipped' && c.preparing_status === 'skipped' && !originalSkippedIds.current.has(c.id)) {
        originalSkippedIds.current.add(c.id);
        updated = true;
      }
    }
    return updated ? new Set(originalSkippedIds.current) : originalSkippedIds.current;
  }, [clips]);

  const isSkippedClip = useCallback((clip: typeof clips[0]) => skippedIdSet.has(clip.id), [skippedIdSet]);

  const skippedClips = useMemo(() => clips.filter(c => isSkippedClip(c) && !hiddenVersionIds.has(c.id)).sort(sortByOrder), [clips, isSkippedClip, hiddenVersionIds]);
  const activeClips = useMemo(() => clips.filter(c => !isSkippedClip(c) && !hiddenVersionIds.has(c.id)).sort(sortByOrder), [clips, isSkippedClip, hiddenVersionIds]);

  const toggleClipSelection = (clipId: string) => {
    setSelectedClips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clipId)) newSet.delete(clipId); else newSet.add(clipId);
      return newSet;
    });
  };

  const selectableClips = useMemo(() => {
    const selectableActive = activeClips.filter(clip => {
      const isProcessing = ['processing', 'pre-queued', 'queued'].includes(clip.clipping_status) || ['processing', 'pre-queued', 'queued'].includes(clip.preparing_status) || ['processing', 'pre-queued', 'queued'].includes(clip.rendering_status);
      const currentStatus = clip.rendering_status !== 'skipped' ? clip.rendering_status : clip.preparing_status !== 'skipped' ? clip.preparing_status : clip.clipping_status;
      return !isProcessing && currentStatus !== 'failure' && currentStatus !== 'expired';
    });
    return [...skippedClips, ...selectableActive];
  }, [activeClips, skippedClips]);

  const downloadableSelectedClips = useMemo(() => clips.filter(clip => {
    if (!selectedClips.has(clip.id)) return false;
    const downloadUrl = clip.finished_video_url || clip.compressed_video_url || clip.clipped_video_url;
    return !!downloadUrl;
  }), [clips, selectedClips]);

  const exportableSelectedClips = useMemo(() => {
    return clips.filter(clip => {
      if (!selectedClips.has(clip.id)) return false;
      const isProcessing = ['processing', 'pre-queued', 'queued'].includes(clip.rendering_status);
      return !isProcessing;
    });
  }, [clips, selectedClips]);

  const deletableSelectedClips = useMemo(() => {
    return clips.filter(clip => {
      if (!selectedClips.has(clip.id)) return false;
      const isProcessing = ['processing', 'pre-queued', 'queued'].includes(clip.clipping_status) ||
        ['processing', 'pre-queued', 'queued'].includes(clip.preparing_status) ||
        ['processing', 'pre-queued', 'queued'].includes(clip.rendering_status);
      return !isProcessing;
    });
  }, [clips, selectedClips]);

  const handleExportSelected = async () => {
    if (!sourceReady || isExporting || exportableSelectedClips.length === 0) return;
    if (!hasProFeatures && exportableSelectedClips.length > 1) {
      setBulkProDialogOpen(true);
      return;
    }
    setIsExporting(true);
    const totalClips = exportableSelectedClips.length;
    setExportProgress({ current: 0, total: totalClips });
    let successCount = 0;
    let errorCount = 0;

    const input: Record<string, any> = {
      aspect_ratio_format: 'vertical',
      subtitles_enabled: subtitlesEnabled,
    };
    input.watermark_position = watermarkPosition ?? DEFAULT_WATERMARK_POSITION;
    if (selectedBrandKitId) {
      input.brand_kit_id = selectedBrandKitId;
    }
    if (subtitlesEnabled) {
      // subtitleData.fontFamily may hold the font's display name (picker /
      // restored config); the render contract wants the documentId.
      const renderFont = resolveRenderFont({
        selectedFontId: selectedSubtitleFontId,
        fontFamily: subtitleData.fontFamily,
        fonts: fontsData?.myFonts,
      });
      input.subtitle_config = {
        fontFamily: renderFont.fontFamily,
        fontUrl: renderFont.fontUrl,
        fontSize: subtitleData.fontSize,
        textColor: subtitleData.textColor,
        highlightColor: subtitleData.highlightColor,
        highlightEnabled: subtitleData.highlightEnabled,
        shadowEnabled: subtitleData.shadowEnabled,
        shadowColor: subtitleData.shadowColor,
        shadowSize: subtitleData.shadowSize,
        ...pickSubtitleOutline(subtitleData),
        backgroundEnabled: subtitleData.backgroundEnabled,
        backgroundColor: subtitleData.backgroundColor,
        highlightBoxEnabled: subtitleData.highlightBoxEnabled,
        highlightBoxBackgroundColor: subtitleData.highlightBoxBackgroundColor,
        highlightBoxBorderRadius: subtitleData.highlightBoxBorderRadius,
        animation: subtitleData.animation,
        marginVertical: subtitleData.marginVertical,
        alignItems: getAlignItems(subtitleData.position),
      };
    }
    if (logoData.url) {
      input.logo_config = {
        logo_image_url: logoData.url,
        logo_position: logoData.position,
        logo_size: logoData.size,
        logo_opacity: logoData.opacity,
        logo_horizontal_margin: logoData.horizontalMargin,
        logo_vertical_margin: logoData.verticalMargin,
      };
    }

    for (let i = 0; i < exportableSelectedClips.length; i++) {
      const clip = exportableSelectedClips[i];
      setExportProgress({ current: i + 1, total: totalClips });
      try {
        await renderLibraryClipMutation({
          variables: {
            id: clip.id,
            input: {
              ...input,
              ...boundLayoutRenderInputForClip(clip),
            },
          },
        });
        successCount++;
      } catch (error) {
        console.error(`Error exporting clip ${clip.id}:`, error);
        if (handleQuotaError(error)) {
          setIsExporting(false);
          setExportProgress({ current: 0, total: 0 });
          return;
        }
        errorCount++;
      }
    }

    setIsExporting(false);
    setExportProgress({ current: 0, total: 0 });

    if (errorCount === 0) {
      toast({ title: t('projectClips.toast.exportStarted'), description: t('projectClips.toast.exportingClips', { count: successCount }) });
    } else if (successCount > 0) {
      toast({ title: t('projectClips.toast.exportPartial'), description: t('projectClips.toast.exportingWithErrors', { success: successCount, errors: errorCount }), variant: "destructive" });
    } else {
      toast({ title: t('projectClips.toast.exportError'), description: t('projectClips.toast.couldNotExport'), variant: "destructive" });
    }
    refetch({ limit: clipsCountRef.current + PAGE_SIZE, offset: 0 });
  };

  const handleExportSingle = useCallback(async (clipId: string) => {
    if (!sourceReady) return;
    setExportingClipIds(prev => new Set(prev).add(clipId));
    const clip = clips.find((candidate) => candidate.id === clipId);
    const input: Record<string, any> = {
      aspect_ratio_format: 'vertical',
      subtitles_enabled: subtitlesEnabled,
    };
    input.watermark_position = watermarkPosition ?? DEFAULT_WATERMARK_POSITION;
    if (selectedBrandKitId) {
      input.brand_kit_id = selectedBrandKitId;
    }
    Object.assign(input, boundLayoutRenderInputForClip(clip));
    if (subtitlesEnabled) {
      // See handleExportSelected — the render contract wants the documentId.
      const renderFont = resolveRenderFont({
        selectedFontId: selectedSubtitleFontId,
        fontFamily: subtitleData.fontFamily,
        fonts: fontsData?.myFonts,
      });
      input.subtitle_config = {
        fontFamily: renderFont.fontFamily,
        fontUrl: renderFont.fontUrl,
        fontSize: subtitleData.fontSize,
        textColor: subtitleData.textColor,
        highlightColor: subtitleData.highlightColor,
        highlightEnabled: subtitleData.highlightEnabled,
        shadowEnabled: subtitleData.shadowEnabled,
        shadowColor: subtitleData.shadowColor,
        shadowSize: subtitleData.shadowSize,
        ...pickSubtitleOutline(subtitleData),
        backgroundEnabled: subtitleData.backgroundEnabled,
        backgroundColor: subtitleData.backgroundColor,
        highlightBoxEnabled: subtitleData.highlightBoxEnabled,
        highlightBoxBackgroundColor: subtitleData.highlightBoxBackgroundColor,
        highlightBoxBorderRadius: subtitleData.highlightBoxBorderRadius,
        animation: subtitleData.animation,
        marginVertical: subtitleData.marginVertical,
        alignItems: getAlignItems(subtitleData.position),
      };
    }
    if (logoData.url) {
      input.logo_config = {
        logo_image_url: logoData.url,
        logo_position: logoData.position,
        logo_size: logoData.size,
        logo_opacity: logoData.opacity,
        logo_horizontal_margin: logoData.horizontalMargin,
        logo_vertical_margin: logoData.verticalMargin,
      };
    }
    try {
      await renderLibraryClipMutation({ variables: { id: clipId, input } });
      toast({ title: t('projectClips.toast.exportStarted') });
      refetch({ limit: clipsCountRef.current + PAGE_SIZE, offset: 0 });
    } catch (error) {
      console.error(`Error exporting clip ${clipId}:`, error);
      if (!handleQuotaError(error)) {
        toast({ title: t('projectClips.toast.exportError'), variant: "destructive" });
      }
    } finally {
      setExportingClipIds(prev => {
        const next = new Set(prev);
        next.delete(clipId);
        return next;
      });
    }
  }, [clips, watermarkPosition, subtitlesEnabled, selectedBrandKitId, boundLayoutRenderInputForClip, subtitleData, logoData, selectedSubtitleFontId, fontsData?.myFonts, renderLibraryClipMutation, toast, t, refetch, handleQuotaError, sourceReady]);

  const toggleSelectAll = () => {
    if (!hasProFeatures) {
      setBulkProDialogOpen(true);
      return;
    }
    if (selectedClips.size === selectableClips.length && selectableClips.length > 0) setSelectedClips(new Set());
    else setSelectedClips(new Set(selectableClips.map(c => c.id)));
  };

  const handleDownloadAll = async () => {
    if (isDownloadingAll || downloadableSelectedClips.length === 0) return;
    setIsDownloadingAll(true);
    const selectedClipsList = downloadableSelectedClips;
    const totalClips = selectedClipsList.length;
    setDownloadProgress({ current: 0, total: totalClips });
    let successCount = 0;
    let failedCount = 0;
    for (let i = 0; i < selectedClipsList.length; i++) {
      const clip = selectedClipsList[i];
      const effectiveDownloadUrl = clip.finished_video_url || clip.compressed_video_url || clip.clipped_video_url;
      setDownloadProgress({ current: i + 1, total: totalClips });
      if (!effectiveDownloadUrl) { failedCount++; continue; }
      try {
        const displayName = clip.name || `Clipe #${i + 1}`;
        await downloadClipVideo({
          clipId: clip.id,
          fallbackName: displayName,
          downloadUrl: effectiveDownloadUrl,
        });
        successCount++;
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download clip ${clip.id}:`, error);
        failedCount++;
      }
    }
    setIsDownloadingAll(false);
    setDownloadProgress({ current: 0, total: 0 });
    if (successCount > 0) {
      toast({ title: t('projectClips.toast.downloadCompleted'), description: t('projectClips.toast.videosDownloaded', { success: successCount, failed: failedCount > 0 ? t('projectClips.toast.failed', { count: failedCount }) : '' }) });
      // One batch is one download event: counting each clip would burn every
      // milestone in a single session.
      notifyClipDownloaded();
    } else {
      toast({ title: t('projectClips.toast.downloadError'), description: t('projectClips.toast.couldNotDownload'), variant: "destructive" });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteSelected = async () => {
    if (isDeleting || deletableSelectedClips.length === 0) return;
    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;
    for (const clip of deletableSelectedClips) {
      try {
        await deleteMyClipMutation({ variables: { id: clip.id } });
        successCount++;
      } catch (error) {
        console.error(`Failed to delete clip ${clip.id}:`, error);
        errorCount++;
      }
    }
    setIsDeleting(false);
    setSelectedClips(new Set());
    if (successCount > 0) {
      toast({ title: t('projectClips.toast.deleteCompleted', { count: successCount }) });
    }
    if (errorCount > 0) {
      toast({ title: t('projectClips.toast.deleteError', { count: errorCount }), variant: "destructive" });
    }
    refetch({ limit: clipsCountRef.current, offset: 0 });
  };

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore || !libraryId) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = clips.length;
      const res = await fetchMore({
        variables: { limit: PAGE_SIZE, offset: prevCount, library_id: libraryId },
        updateQuery: appendFetchMoreList('myClips'),
      });
      if ((res.data?.myClips?.length ?? 0) < PAGE_SIZE) setHasMore(false);
    } catch (loadMoreError) {
      console.error('Failed to load more library clips', loadMoreError);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    if (docH - (currentY + viewportH) < 600) handleLoadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, hasMore, loading]);

  useEffect(() => {
    if (!loading && clips.length > 0 && clips.length < PAGE_SIZE) setHasMore(false);
     
  }, [loading, clips.length]);

  useEffect(() => {
    const hasProcessingClips = clips.some(clip =>
      ['processing', 'pre-queued', 'queued'].includes(clip.clipping_status) || ['processing', 'pre-queued', 'queued'].includes(clip.preparing_status) || ['processing', 'pre-queued', 'queued'].includes(clip.rendering_status)
    );
    if (!hasProcessingClips) return;
    const pollInterval = setInterval(() => refetch({ limit: clipsCountRef.current, offset: 0 }), 5000);
    return () => clearInterval(pollInterval);
  }, [clips, refetch]);

  const handleEditVideo = useCallback((clipId: string) => {
    navigate(`/library/${libraryId}/edit?clip=${clipId}`);
  }, [navigate, libraryId]);

  const { enabled: hasAiAssistant } = useAiAssistantAccess();
  const handleEditWithAi = useCallback((clipId: string) => {
    if (!libraryId) return;
    navigate(aiStudioPathFor({ kind: "library", id: libraryId, clipId }));
  }, [navigate, libraryId]);

  // Scene/face detection is still running if the library has loaded but has no detection data
  const isDetectionPending = !libraryLoading && !!library && !library.scene_detection_url && !library.scene_detection;

  // Poll library data while detection or initial curation is still processing
  const isCurationPending = !!library && library.curation_status != null
    && !['success', 'failure', 'expired'].includes(library.curation_status);
  useEffect(() => {
    if (!isDetectionPending && !isCurationPending && !shouldPollClipSource(sourceStatus)) return;
    const interval = setInterval(() => refetchLibrary(), 10000);
    return () => clearInterval(interval);
  }, [isDetectionPending, isCurationPending, sourceStatus, refetchLibrary]);

  // Refetch clips when the library transitions from processing to success.
  // The clips query runs once on mount; without this, a library that was
  // empty during processing would stay empty in the UI even after the
  // backend populates it (e.g. free-user clone reveal).
  const prevCurationStatusRef = useRef(library?.curation_status);
  useEffect(() => {
    const prev = prevCurationStatusRef.current;
    const curr = library?.curation_status;
    if (prev && prev !== 'success' && curr === 'success') {
      refetch();
    }
    prevCurationStatusRef.current = curr;
  }, [library?.curation_status, refetch]);

  const libraryStatus = library?.curation_status;
  const loadingKind = resolveClipsPageLoadingKind({
    resourceLoading: libraryLoading,
    status: libraryStatus,
    clipsLoading: loading,
  });
  const isInitialLoading = loadingKind === 'clips' || loadingKind === 'unknown';
  const showSceneDetectionWarning = !isInitialLoading && !!library?.scene_detection_url && (sceneDetectionStatus === 'error' || sceneDetectionStatus === 'timeout');
  const showTranslationFailed = !isInitialLoading && shouldShowTranslationFailed(library);
  const showTranscriptionWarning = !isInitialLoading && !!(library as any)?.transcription_url && (transcriptionStatus === 'error' || transcriptionStatus === 'timeout');

  // Copilot libraries land on /edit; non-copilot libraries on /clips.
  const libraryProcessingMode = (library as any)?.processing_mode;
  if (!libraryLoading && !library) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('projectEditor.projectNotFound')}</p>
        </div>
      </Layout>
    );
  }
  if (libraryStatus === 'failure') {
    return (
      <Layout>
        <FailedProjectNotice
          kind="library"
          id={libraryId}
          failureCode={library?.failure_code}
          videoUrl={library?.compressed_base_video_url}
          durationSeconds={library?.duration_seconds}
          rangeStart={library?.start_time}
          onConverted={() => {
            refetchLibrary();
            refetch();
          }}
        />
      </Layout>
    );
  }
  if (libraryStatus === 'expired') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('projectEditor.projectExpired')}</p>
        </div>
      </Layout>
    );
  }
  if (libraryStatus && libraryStatus !== 'success') {
    const etaMinutes = etaSecondsToMinutes(library?.eta_seconds);
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <ProcessingStatus
            status={libraryStatus}
            statusLabel={getProjectStatusLabel(libraryStatus)}
            etaLabel={etaMinutes != null
              ? t('projectEditor.estimatedTime', { minutes: etaMinutes })
              : undefined}
            description={t('projectEditor.processingDescription')}
            backLabel={t('projectEditor.backToLibrary')}
            onBack={() => navigate('/library')}
          />
        </div>
      </Layout>
    );
  }
  if (sourceReady && libraryStatus === 'success' && libraryProcessingMode === 'copilot') {
    return <Navigate to={`/library/${libraryId}/edit`} replace />;
  }

  if (loadingKind === 'unknown') {
    return (
      <Layout>
        <LoadingCardFrame>
          <LoadingCard message={t('projectClips.loading')} />
        </LoadingCardFrame>
      </Layout>
    );
  }

  if (loadingKind === 'clips') {
    const clipsLoadingSteps = resolveClipsLoadingSteps({
      resourceLoaded: !libraryLoading,
      clipsLoaded: !loading,
      sceneLoaded: !!sceneDetection,
      transcriptionLoaded: !!transcription,
    }).map((step) => ({
      ...step,
      label: t(`projectClips.loadingSteps.${step.id === 'resource' ? 'library' : step.id === 'scene' ? 'sceneDetection' : step.id}`),
    }));

    return (
      <Layout>
        <LoadingCardFrame>
          <ClipsLoadingStatus
            title={t('projectClips.loadingSteps.title')}
            steps={clipsLoadingSteps}
          />
        </LoadingCardFrame>
      </Layout>
    );
  }

  return (
    <WatermarkPositionContext.Provider value={watermarkPosition}>
    <Layout>
      <div className="flex-1 p-4 lg:p-6">
        {!isInitialLoading && (
          <>
        <h2 className="text-xl lg:text-2xl font-bold mb-6">
          {library ? t('projectClips.clipsReady') : t('projectClips.clipsReady')}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('projectClips.clipsOptimized', { count: clips.length })}
        </p>

        <ClipSourceNotice status={sourceStatus} />

        {/* Detection in progress banner */}
        {isDetectionPending && (
          <div className="mb-6 p-4 bg-card rounded-lg border border-amber-500/30 flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-amber-500 animate-spin flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">{t('projectClips.detectionInProgress.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('projectClips.detectionInProgress.description')}</p>
            </div>
          </div>
        )}

        {showSceneDetectionWarning && (
          <div className="mb-6 p-4 bg-card rounded-lg border border-yellow-500/30 flex items-start gap-3">
            <div>
              <p className="text-sm font-medium">{t('projectClips.sceneDetectionUnavailable.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {sceneDetectionTimedOut ? t('projectClips.sceneDetectionUnavailable.timeoutDescription') : t('projectClips.sceneDetectionUnavailable.description')}
              </p>
              {sceneDetectionError && (
                <p className="text-xs text-muted-foreground mt-1 opacity-80">{sceneDetectionError.message}</p>
              )}
            </div>
          </div>
        )}

        {showTranscriptionWarning && (
          <div className="mb-6 p-4 bg-card rounded-lg border border-yellow-500/30 flex items-start gap-3">
            <div>
              <p className="text-sm font-medium">{t('projectClips.transcriptionUnavailable.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {transcriptionTimedOut ? t('projectClips.transcriptionUnavailable.timeoutDescription') : t('projectClips.transcriptionUnavailable.description')}
              </p>
              {transcriptionError && (
                <p className="text-xs text-muted-foreground mt-1 opacity-80">{transcriptionError.message}</p>
              )}
            </div>
          </div>
        )}

        {showTranslationFailed && (
          <div className="mb-6 p-4 bg-card rounded-lg border border-yellow-500/30 flex items-start gap-3">
            <p className="text-sm font-medium">{t('projectClips.translationFailed')}</p>
          </div>
        )}

        {/* Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 bg-card rounded-lg border sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={toggleSelectAll} className="border-primary/20">
              {selectedClips.size === selectableClips.length ? t('projectClips.deselectAll') : t('projectClips.selectAll')}
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('projectClips.selectedOf', { selected: selectedClips.size, total: clips.length })}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={subtitlesEnabled}
                onCheckedChange={setSubtitlesEnabled}
                className="data-[state=checked]:bg-primary"
              />
              <Label className="text-xs text-muted-foreground">
                {t("projectClips.styleConfig.subtitlesToggle")}
              </Label>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStyleSheetOpen(true)}
              className="flex items-center gap-2 border-primary/20"
            >
              <Palette className="w-4 h-4" />
              <span className="hidden md:inline truncate max-w-[220px]">
                {t('projectClips.styleConfig.stylize')}
              </span>
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              disabled={!sourceReady || exportableSelectedClips.length === 0 || isExporting}
              onClick={handleExportSelected}
              className="flex items-center gap-2"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              {isExporting ? t('projectClips.exporting') : t('projectClips.exportSelected', { count: exportableSelectedClips.length })}
            </Button>
            <Button
              variant="gradient" size="sm"
              disabled={downloadableSelectedClips.length === 0 || isDownloadingAll}
              onClick={handleDownloadAll}
              className="flex items-center gap-2"
            >
              <Download className={`w-4 h-4 ${isDownloadingAll ? 'animate-bounce' : ''}`} />
              {isDownloadingAll ? t('projectClips.downloading') : t('projectClips.downloadAll', { count: downloadableSelectedClips.length })}
            </Button>
          </div>
        </div>

        {brandKits.length > 0 && (
          <div className="mb-6" data-testid="library-clips-templates">
            <div className="flex items-center gap-1.5 mb-2">
              <Palette className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">
                {t('projectEditor.templatesSection')}
              </span>
            </div>
            <EditorTemplatesCarousel
              kits={brandKits}
              selectedId={selectedBrandKitId}
              onSelect={(id) => {
                const kit = brandKits.find((candidate: { id: string }) => candidate.id === id);
                if (kit) handleSelectBrandKit(kit);
              }}
            />
          </div>
        )}

        <ClipsStyleSheet
          watermarkPosition={watermarkPosition}
          onWatermarkPositionChange={setWatermarkPosition}
          onWatermarkPositionReset={resetWatermarkPosition}
          open={styleSheetOpen}
          onOpenChange={setStyleSheetOpen}
          subtitlesEnabled={subtitlesEnabled}
          onSubtitlesEnabledChange={setSubtitlesEnabled}
          userBrandKits={userBrandKits}
          publicBrandKits={publicBrandKits}
          selectedBrandKitId={selectedBrandKitId}
          onSelectBrandKit={handleSelectBrandKit}
          subtitleData={subtitleData}
          updateSubtitle={updateSubtitle}
          selectedSubtitleFontId={selectedSubtitleFontId}
          onSelectSubtitleFont={handleSelectSubtitleFont}
          getFontFamilyName={getFontFamilyName}
          logoData={logoData}
          updateLogo={updateLogo}
          onLogoUpload={handleLogoUpload}
          isUploadingLogo={isUploadingLogo}
          enforcedSubtitlePos={enforcedSubtitlePos}
          fallbackPreviewKit={fallbackPreviewKit}
          videoUrl={sourceReady ? libraryVideoUrl : undefined}
          previewProcessing={firstPreviewProcessing}
          cropCenterKeyframes={cropCenterKeyframes}
          previewSpeakerLayout={previewSpeakerLayout}
          subtitleConfig={subtitleConfig}
          logoConfig={logoConfig}
          previewTranscriptionSegments={previewTranscriptionSegments}
          postLayout={postLayout}
          effectivePostLayout={effectivePostLayout}
          uniqueSpeakerCount={uniqueSpeakers.length}
          selectedSpeakerLayout={selectedSpeakerLayout}
          onSelectSpeakerLayout={(next) => {
            setPostLayout(null);
            setLayoutFieldBinds({});
            handleSpeakerLayoutChange(next);
          }}
          onSelectPostLayout={selectPostLayout}
          onUpdatePostLayoutField={updatePostLayoutField}
          postLayoutDisabledKeys={effectivePostLayout ? enforcedLockedKeys(effectivePostLayout) : undefined}
          fieldBinds={layoutFieldBinds}
          onFieldBindChange={handleFieldBindChange}
          bindContext={bindContext}
          fillerPool={fillerPool}
          previewClipDurationSec={previewFillerDurationSec}
          onPostLayoutPatch={(patch) => {
            setPostLayout((prev) => (
              prev ? { ...prev, ...patch } as typeof prev : prev
            ));
          }}
          showWatermark={showWatermark}
          libraryDocumentId={libraryId}
        />

          {/* Export Progress Bar */}
          {isExporting && exportProgress.total > 0 && (
            <div className="mb-6 p-4 bg-card rounded-lg border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t('projectClips.exportingVideos')}</span>
                <span className="text-sm text-muted-foreground">{t('projectClips.ofTotal', { current: exportProgress.current, total: exportProgress.total })}</span>
              </div>
              <Progress value={(exportProgress.current / exportProgress.total) * 100} className="h-2" />
            </div>
          )}

          {/* Download Progress Bar */}
          {isDownloadingAll && downloadProgress.total > 0 && (
            <div className="mb-6 p-4 bg-card rounded-lg border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t('projectClips.downloadingVideos')}</span>
                <span className="text-sm text-muted-foreground">{t('projectClips.ofTotal', { current: downloadProgress.current, total: downloadProgress.total })}</span>
              </div>
              <Progress value={(downloadProgress.current / downloadProgress.total) * 100} className="h-2" />
            </div>
          )}

        {error && (
          <div className="text-sm text-destructive">{t('projectClips.failedToLoadClips')}</div>
        )}

        {!error && (
          <>
            {skippedClips.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-muted-foreground">
                  {t('projectClips.previewClips')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {skippedClips.map((clip, index) => (
                    <SkippedClipCard
                      key={clip.id}
                      clip={clip}
                      clipNumber={index + 1}
                      videoUrl={sourceReady ? libraryVideoUrl : undefined}
                      previewProcessing={previewProcessingByClipId.get(clip.id)}
                      cropCenterKeyframes={cropCenterKeyframes}
                      isPreviewActive={mountedPreviewClipId === clip.id}
                      onPreviewPlay={handlePreviewPlay}
                      onPreviewPause={handlePreviewPause}
                      isSelected={selectedClips.has(clip.id)}
                      onToggleSelection={toggleClipSelection}
                      logoConfig={logoConfig}
                      subtitlesEnabled={subtitlesEnabled}
                      subtitleConfig={subtitleConfig}
                      onEditVideo={sourceReady ? handleEditVideo : undefined}
                      onEditWithAi={sourceReady && hasAiAssistant ? handleEditWithAi : undefined}
                      onExport={sourceReady ? handleExportSingle : undefined}
                      isExporting={exportingClipIds.has(clip.id)}
                      transcriptionWords={transcriptionWords}
                      projectDuration={library?.duration_seconds}
                      speakerLayout={previewSpeakerLayout}
                      postLayout={boundPostLayoutForClip(clip.name)}
                      showWatermark={showWatermark}
                      versionGroup={getGroupForClip(clip.id)}
                      onSendToTikTok={resolvePublishHandler(
                        !!clip.finished_video_url && ![
                          clip.clipping_status,
                          clip.preparing_status,
                          clip.rendering_status,
                        ].includes('expired'),
                        'tiktok',
                      )}
                      onPublishToYouTube={resolvePublishHandler(
                        !!clip.finished_video_url && ![
                          clip.clipping_status,
                          clip.preparing_status,
                          clip.rendering_status,
                        ].includes('expired'),
                        'youtube',
                      )}
                      onPublishToInstagram={resolvePublishHandler(
                        !!clip.finished_video_url && ![
                          clip.clipping_status,
                          clip.preparing_status,
                          clip.rendering_status,
                        ].includes('expired'),
                          'instagram',
                      )}
                      onPublishToFacebook={resolvePublishHandler(
                        !!clip.finished_video_url && ![
                          clip.clipping_status,
                          clip.preparing_status,
                          clip.rendering_status,
                        ].includes('expired'),
                        'facebook',
                      )}
                      tiktokPublishStatus={getPublishStatus(clip.id, 'tiktok')}
                      tiktokPublishFailReason={getPublishFailReason(clip.id, 'tiktok')}
                      youtubePublishStatus={getPublishStatus(clip.id, 'youtube')}
                      youtubePublishFailReason={getPublishFailReason(clip.id, 'youtube')}
                      instagramPublishStatus={getPublishStatus(clip.id, 'instagram')}
                      instagramPublishFailReason={getPublishFailReason(clip.id, 'instagram')}
                      facebookPublishStatus={getPublishStatus(clip.id, 'facebook')}
                      facebookPublishFailReason={getPublishFailReason(clip.id, 'facebook')}
                      publishMediaKinds={getPublishMediaKinds(clip.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {activeClips.map((clip, index) => (
                <VerticalClipCard
                  key={clip.id}
                  clip={clip}
                  clipNumber={index + 1}
                  isSelected={selectedClips.has(clip.id)}
                  onToggleSelection={toggleClipSelection}
                  onEditVideo={sourceReady ? handleEditVideo : undefined}
                  onEditWithAi={sourceReady && hasAiAssistant ? handleEditWithAi : undefined}
                  onExport={sourceReady ? handleExportSingle : undefined}
                  isExporting={exportingClipIds.has(clip.id)}
                  isPreviewActive={mountedPreviewClipId === clip.id}
                  onPreviewPlay={handlePreviewPlay}
                  onPreviewPause={handlePreviewPause}
                  videoUrl={sourceReady ? libraryVideoUrl : undefined}
                  previewProcessing={previewProcessingByClipId.get(clip.id)}
                  cropCenterKeyframes={cropCenterKeyframes}
                  logoConfig={logoConfig}
                  subtitlesEnabled={subtitlesEnabled}
                  subtitleConfig={subtitleConfig}
                  transcriptionWords={transcriptionWords}
                  projectDuration={library?.duration_seconds}
                  speakerLayout={previewSpeakerLayout}
                  postLayout={boundPostLayoutForClip(clip.name)}
                  showWatermark={showWatermark}
                  versionGroup={getGroupForClip(clip.id)}
                  onSendToQueue={hasSocialPublishing ? handleSendToQueue : undefined}
                  onSendToTikTok={resolvePublishHandler(
                    !!clip.finished_video_url && ![
                      clip.clipping_status,
                      clip.preparing_status,
                      clip.rendering_status,
                    ].includes('expired'),
                    'tiktok',
                  )}
                  onPublishToYouTube={resolvePublishHandler(
                    !!clip.finished_video_url && ![
                      clip.clipping_status,
                      clip.preparing_status,
                      clip.rendering_status,
                    ].includes('expired'),
                    'youtube',
                  )}
                  onPublishToInstagram={resolvePublishHandler(
                    !!clip.finished_video_url && ![
                      clip.clipping_status,
                      clip.preparing_status,
                      clip.rendering_status,
                    ].includes('expired'),
                    'instagram',
                  )}
                  onPublishToFacebook={resolvePublishHandler(
                    !!clip.finished_video_url && ![
                      clip.clipping_status,
                      clip.preparing_status,
                      clip.rendering_status,
                    ].includes('expired'),
                    'facebook',
                  )}
                  tiktokPublishStatus={getPublishStatus(clip.id, 'tiktok')}
                  tiktokPublishFailReason={getPublishFailReason(clip.id, 'tiktok')}
                  youtubePublishStatus={getPublishStatus(clip.id, 'youtube')}
                  youtubePublishFailReason={getPublishFailReason(clip.id, 'youtube')}
                  instagramPublishStatus={getPublishStatus(clip.id, 'instagram')}
                  instagramPublishFailReason={getPublishFailReason(clip.id, 'instagram')}
                  facebookPublishStatus={getPublishStatus(clip.id, 'facebook')}
                  facebookPublishFailReason={getPublishFailReason(clip.id, 'facebook')}
                  publishMediaKinds={getPublishMediaKinds(clip.id)}
                />
              ))}
              {clips.length === 0 && (
                <div className="col-span-full text-center text-sm text-muted-foreground py-12">
                  {t('projectClips.noClipsFound')}
                </div>
              )}
              {isFetchingMore && (
                <div className="col-span-full text-center text-xs text-muted-foreground py-2">
                  {t('projectClips.loadingMore')}
                </div>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>
          {quotaUpsellDialog}
          <ProFeatureDialog
        open={bulkProDialogOpen}
        onOpenChange={setBulkProDialogOpen}
        onUpgrade={() => navigate('/plans')}
        title={t('projectEditor.bulkExportProTitle')}
        body={t('projectEditor.bulkExportProBody')}
        dismissLabel={t('projectEditor.layers.proExportDialogDismiss')}
        upgradeLabel={t('projectEditor.layers.proExportDialogUpgrade')}
        testIdPrefix="bulk-pro"
      />
      <ClipPublishDialogs
        showPublishProGate={showPublishProGate}
        onPublishProGateChange={setShowPublishProGate}
        publishTarget={publishTarget}
        onPublishTargetChange={setPublishTarget}
        resolveClipTitle={resolveClipTitle}
        onPublished={refetchPublishes}
      />
      {queueTargetClipId ? (
        <SendToQueueDialog
          open
          clipId={queueTargetClipId}
          clipTitle={resolveClipTitle(queueTargetClipId)}
          onOpenChange={setQueueDialogOpen}
        />
      ) : null}
    </Layout>
    </WatermarkPositionContext.Provider>
  );
};

export default LibraryClips;
